import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiAlertCircle } from 'react-icons/fi';
import taxesService from '../../../../services/taxes.service';
import SectionHelpModal from '../../../../components/common/SectionHelpModal';
import { useAuth } from '../../../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import AppTour from '../../../../components/common/AppTour';
import Pagination from '../../../../components/common/Pagination';
const TaxManagement = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [taxes, setTaxes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editingTax, setEditingTax] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        porcentaje: '',
        activo: true
    });
    const [runTour, setRunTour] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tour') === 'impuestos') {
            setRunTour(true);
        }
    }, [location.search]);

    useEffect(() => {
        fetchTaxes(currentPage);
    }, [currentPage]);

    const fetchTaxes = async (page = 1) => {
        try {
            setLoading(true);
            const data = await taxesService.getAll({ page });
            
            if (data?.results) {
                setTaxes(data.results);
                setTotalCount(data.count || 0);
            } else if (Array.isArray(data)) {
                setTaxes(data);
                setTotalCount(data.length);
            } else {
                setTaxes([]);
                setTotalCount(0);
            }
        } catch (err) {
            console.error('Error fetching taxes:', err);
            setError('Error al cargar impuestos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            if (editingTax) {
                await taxesService.update(editingTax.id, formData);
                setSuccess('Impuesto actualizado correctamente');
            } else {
                await taxesService.create(formData);
                setSuccess('Impuesto creado correctamente');
            }

            fetchTaxes();
            handleCancel();
        } catch (err) {
            console.error('Error saving tax:', err);
            setError(err.response?.data?.detail || 'Error al guardar impuesto');
        }
    };

    const handleEdit = (tax) => {
        setEditingTax(tax);
        setFormData({
            nombre: tax.nombre,
            porcentaje: tax.porcentaje,
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar este impuesto?')) {
            return;
        }

        try {
            await taxesService.delete(id);
            setSuccess('Impuesto eliminado correctamente');
            fetchTaxes();
        } catch (err) {
            console.error('Error deleting tax:', err);
            setError('Error al eliminar impuesto');
        }
    };

    const handleToggleActive = async (tax) => {
        try {
            await taxesService.toggleActive(tax.id, !tax.activo);
            setSuccess(`Impuesto ${!tax.activo ? 'activado' : 'desactivado'} correctamente`);
            fetchTaxes();
        } catch (err) {
            console.error('Error toggling tax:', err);
            setError('Error al cambiar estado del impuesto');
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingTax(null);
        setFormData({ nombre: '', porcentaje: '', activo: true });
    };

    const impuestosTourSteps = [
        {
            element: '#tour-impuestos-header',
            intro: '<strong>Panel de Impuestos</strong>Aquí configuras los impuestos que deben cobrarse en tus ventas, como el IVA del 19%.',
            position: 'bottom',
        },
        {
            element: '#tour-impuestos-nuevo',
            intro: '<strong>Crear Impuesto</strong>Ponle un nombre y define su porcentaje. El sistema lo sumará automáticamente al total de tus cotizaciones.',
            position: 'bottom',
        },
        {
            element: '#tour-impuestos-table',
            intro: '<strong>Lista de Impuestos</strong>Activa o desactiva los impuestos que necesitas aplicar con un solo clic.',
            position: 'top',
        }
    ];

    if (loading && taxes.length === 0) {
        return <div className="loading-overlay"><div className="spinner"></div></div>;
    }

    return (
        <div className="container-custom">
            {/* 🌟 Capa del Tour Guiado Inyectada 🌟 */}
            {runTour && (
                <AppTour
                    steps={impuestosTourSteps}
                    runTour={runTour}
                    setRunTour={setRunTour}
                    tourOptions={{ doneLabel: user?.rol === 'ADMIN' ? 'Continuar a Reportes' : 'Terminar recorrido interno' }}
                    onTourComplete={() => {
                        if (user?.rol === 'ADMIN') {
                            window.location.href = '/app/cotizaplus/reportes?tour=reportes';
                        } else {
                            window.location.href = '/app/cotizaplus?tour=final_modulo';
                        }
                    }}
                />
            )}

            <div className="card-header-custom" style={{ marginTop: '20px', borderBottom: 'none' }} id="tour-impuestos-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <h2 style={{ marginBottom: 0 }}>Gestión de Impuestos</h2>
                        <SectionHelpModal
                            title="¿Cómo funcionan los Impuestos?"
                            content={
                                <div>
                                    <p>Aquí configuras los impuestos legales que aplican a tus ventas, como el IVA (19% en Chile) o Retenciones específicas.</p>
                                    <ul style={{ paddingLeft: '20px', marginTop: '15px' }}>
                                        <li style={{ marginBottom: '10px' }}><strong>Cálculo Automático:</strong> Los impuestos que estén marcados como "Activos" se aplicarán automáticamente a los productos que vayas agregando a una nueva cotización.</li>
                                        <li><strong>Precios Netos:</strong> Recuerda que al crear productos, debes ingresar siempre su precio NETO (sin impuesto). Para que luego el sistema le sume correctamente este porcentaje.</li>
                                    </ul>
                                </div>
                            }
                        />
                    </div>
                    <p className="text-muted" style={{ marginTop: '5px' }}>Administra los impuestos predefinidos de tu empresa</p>
                </div>
                <button
                    id="tour-impuestos-nuevo"
                    className="btn-custom btn-primary d-none d-lg-flex"
                    onClick={() => setShowForm(true)}
                    disabled={showForm}
                >
                    <FiPlus size={20} /> Nuevo Impuesto
                </button>
            </div>

            {error && (
                <div className="alert-custom alert-danger">
                    <FiAlertCircle size={20} />
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="alert-custom alert-success">
                    <FiCheck size={20} />
                    <span>{success}</span>
                </div>
            )}

            {/* Formulario */}
            {showForm && (
                <div className="card-custom" style={{ marginBottom: '20px' }}>
                    <h3>{editingTax ? 'Editar Impuesto' : 'Nuevo Impuesto'}</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="grid-1-to-2" style={{ gap: '20px' }}>
                            <div className="form-group-custom">
                                <label className="form-label-custom">Nombre *</label>
                                <input
                                    type="text"
                                    className="form-control-custom"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    required
                                    placeholder="Ej: IVA, Impuesto Específico"
                                />
                            </div>

                            <div className="form-group-custom">
                                <label className="form-label-custom">Porcentaje (%) *</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    className="form-control-custom"
                                    value={formData.porcentaje}
                                    onChange={(e) => setFormData({ ...formData, porcentaje: e.target.value })}
                                    required
                                    placeholder="Ej: 19.00"
                                />
                            </div>
                        </div>



                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button
                                type="button"
                                className="btn-custom"
                                style={{ background: '#f8f9fa', color: '#333', border: '1px solid #ddd' }}
                                onClick={handleCancel}
                            >
                                <FiX size={18} /> Cancelar
                            </button>
                            <button type="submit" className="btn-custom btn-primary">
                                <FiCheck size={18} /> {editingTax ? 'Actualizar' : 'Crear'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Tabla */}
            <div className="card-custom">
                {taxes.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px' }} id="tour-impuestos-table">
                        <p className="text-muted">No hay impuestos registrados</p>
                    </div>
                ) : (
                    <>
                        <div className="table-responsive d-none d-lg-block" id="tour-impuestos-table">
                            <table className="table-custom">
                                <thead>
                                    <tr>
                                        <th>Nombre</th>
                                        <th>Porcentaje</th>
                                        <th>Estado</th>
                                        <th>Última Actualización</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {taxes.map((tax) => (
                                        <tr key={tax.id}>
                                            <td style={{ fontWeight: 'bold' }}>{tax.nombre}</td>
                                            <td>{tax.porcentaje}%</td>
                                            <td>
                                                <span
                                                    className={`badge ${tax.activo ? 'badge-success' : 'badge-secondary'}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleToggleActive(tax)}
                                                >
                                                    {tax.activo ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td>
                                                {new Date(tax.fecha_actualizacion).toLocaleDateString('es-CL')}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <button
                                                        type="button"
                                                        className="btn-custom btn-sm"
                                                        onClick={() => handleEdit(tax)}
                                                        title="Editar"
                                                        style={{ backgroundColor: '#fff', color: 'var(--primary-orange)', border: '1px solid var(--primary-orange)' }}
                                                    >
                                                        <FiEdit2 size={16} /> Editar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-custom btn-sm"
                                                        onClick={() => handleDelete(tax.id)}
                                                        title="Eliminar"
                                                        style={{ backgroundColor: '#fff', color: 'var(--danger-red)', border: '1px solid var(--danger-red)' }}
                                                    >
                                                        <FiTrash2 size={16} /> Eliminar
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-block d-lg-none mt-3">
                            {taxes.map((tax) => (
                                <div key={tax.id} className="mobile-card">
                                    <div className="mobile-card-header">
                                        <span className="mobile-card-title" style={{ marginBottom: 0 }}>{tax.nombre}</span>
                                        <span
                                            className={`badge ${tax.activo ? 'badge-success' : 'badge-secondary'}`}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleToggleActive(tax)}
                                        >
                                            {tax.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </div>
                                    <div className="mobile-card-body">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '1.1rem' }}>
                                            <span style={{ color: 'var(--text-dark)', fontWeight: '500' }}>Porcentaje:</span>
                                            <span style={{ fontWeight: 'bold' }}>{tax.porcentaje}%</span>
                                        </div>
                                        <div className="mobile-card-subtitle" style={{ marginTop: '12px' }}>
                                            Última Actualización: {new Date(tax.fecha_actualizacion).toLocaleDateString('es-CL')}
                                        </div>
                                    </div>
                                    <div className="mobile-card-footer" style={{ borderTop: 'none', paddingTop: 0, justifyContent: 'flex-end', minHeight: 'unseT', paddingBottom: '16px' }}>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button
                                                type="button"
                                                className="btn-custom btn-sm"
                                                onClick={() => handleEdit(tax)}
                                                style={{ backgroundColor: '#fff', color: 'var(--primary-orange)', border: '1px solid var(--primary-orange)' }}
                                            >
                                                <FiEdit2 size={14} /> Editar
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-custom btn-sm"
                                                onClick={() => handleDelete(tax.id)}
                                                style={{ backgroundColor: '#fff', color: 'var(--danger-red)', border: '1px solid var(--danger-red)' }}
                                            >
                                                <FiTrash2 size={14} /> Eliminar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </>
                )}
            </div>

            {/* Botón Flotante para Móviles */}
            <button
                className="fab-custom d-lg-none"
                onClick={() => setShowForm(true)}
                title="Nuevo Impuesto"
                disabled={showForm}
            >
                <FiPlus size={28} />
            </button>
        </div>
    );
};

export default TaxManagement;
